'use client';

import { Scenario } from '@wareena/grid-battles-entity/scenario/entity/scenario';
import Map from '@wareena/ui/game/map';
import { calcMapDimensions, createHexProperties } from '@wareena/game-algorithms';
import { getTerrainHex } from '@wareena/grid-battles-ui/game/terrain/index';
import UnitLayer from '@local/scenario/ui/ScenarioSelector/UnitLayer';
import { SideId } from '@wareena/grid-battles-entity/game/codebook/side';
import Link from 'next/link';
import createPath from '@local/router/createPath';
import { RouteName } from '@local/router/routes';

export default function ScenariosListItem({ scenario }: { scenario: Scenario }) {
    return (
        <article key={scenario.id} className="box scenario-list-item mx-2">
            <div>
                <h2 className="title is-4">
                    <Link
                        href={createPath(RouteName.GameAgainstComputer, {
                            scenarioId: scenario.id,
                        })}
                    >
                        {scenario.name}
                    </Link>
                </h2>
                <div>
                    <ScenarioStats scenario={scenario} />
                </div>
            </div>

            <div className="is-flex is-justify-content-center mt-4">
                <Link href={createPath(RouteName.GameAgainstComputer, { scenarioId: scenario.id })}>
                    <MapPreview scenario={scenario} />
                </Link>
            </div>

            <Link
                href={createPath(RouteName.GameAgainstComputer, {
                    scenarioId: scenario.id,
                })}
                className="button is-info is-large is-fullwidth mt-4"
            >
                Play Game
            </Link>
        </article>
    );
}

function ScenarioStats({ scenario }: { scenario: Scenario }) {
    return (
        <>
            <ul>
                <li>
                    <span className="icon-text">
                        <span className="icon">
                            <i className="fas fa-trophy"></i>
                        </span>
                        <span>{scenario.victoryConditions} enemy units to destroy</span>
                    </span>
                </li>
                <li>
                    <span className="icon-text">
                        <span className="icon">
                            <i className="fas fa-bolt"></i>
                        </span>
                        <span>{scenario.activationsPerTurn} activations per turn</span>
                    </span>
                </li>
                <li>
                    <span className="icon-text">
                        <span className="icon">
                            <i
                                className={`fas fa-flag ${
                                    scenario.startingSide === SideId.A
                                        ? 'has-color-player-a'
                                        : 'has-color-player-b'
                                }`}
                            ></i>
                        </span>
                        <span>
                            {scenario.startingSide === SideId.A
                                ? 'You go first'
                                : 'Computer goes first'}
                        </span>
                    </span>
                </li>
            </ul>
        </>
    );
}

function MapPreview({ scenario }: { scenario: Scenario }) {
    const hexProperties = createHexProperties({
        hexSize: 20,
        hasFlatTop: scenario.hasFlatTop,
        hasOffsetOnFirstLine: scenario.hasOffsetOnFirstLine,
    });

    const mapDimensions = calcMapDimensions(scenario.width, scenario.height, hexProperties);

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="300"
            height="300"
            viewBox={`0 0 ${mapDimensions.width} ${mapDimensions.height}`}
            style={{
                border: 'solid 1px #ededed',
                borderRadius: '4px',
                padding: '0.5rem',
            }}
        >
            <Map hexProperties={hexProperties} map={scenario.hexes} getTerrainHex={getTerrainHex} />
            <UnitLayer hexProperties={hexProperties} units={scenario.units} />
        </svg>
    );
}
